import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
dayjs.extend(utc);
dayjs.extend(timezone);
if(window.weglot_lang== 'en'){
  dayjs.locale('en') // use locale globally
}else{
  dayjs.locale('fr') // use locale globally
}
export default {
    searchClient: null,
    gsap: null,
    utils: null,
    algoliaConfig: null,

    userTimezone: null,

    currentUiState: null,

    /**
     * Filter the query with numeric, facet, or tag filters.
     * It has a high priority over widget faceting.
     *
     * @link https://www.algolia.com/doc/api-reference/api-parameters/filters/?client=javascript
     *
     * @type {string}
     */
    searchParameters_filters: '',

    /**
     * Filter hits by facet value.
     * It has a low priority over widget faceting, it's like they have 'OR' operator => meaning you can get result outside what you put in facetFilters.
     *
     * It’s recommended that you use the filters parameter instead of facetFilters since filters has a more straightforward, SQL-like syntax, and supports both filters and facets.
     *
     * @link https://www.algolia.com/doc/api-reference/api-parameters/facetFilters/?client=javascript
     *
     * @type {*[]}
     */
    searchParameters_facetFilters: [],

    favorites: null,
    favorites_program: null,
    favorites_exhibitor: null,
    favorites_product: null,
    favorites_all: null,
    favorites_all_filter: null,

    event_list_updated: null,
    event_campaign_update: null,
    campaignTemplate: '',
    
    // When using dynamicWidgets
    dynamicWidgets_containersList: [],

    // Global custom connectors
    customRefinementList: null,
    customHierarchicalMenu: null,

    // For managing the title section of lists like exhibitors
    currentSection: '',
    currentLine: 0,
    currentLine_itemCount: 0,

    init: function (utils, gsap) {
        this.algoliaConfig = window.algolia_json ?? {};

        // Early checks
        const indexName = this.algoliaConfig.indexName ?? null;
        if (!indexName) {
            console.log("algolia_index_name n'est pas défini !");
            return;
        }

        this.utils = utils;
        this.gsap = gsap;
        this.event_list_updated = new CustomEvent('list:updated');
        this.event_campaign_update = new CustomEvent('campaign:updated');
        const campaign_template = document.querySelector('.js-campaign-template');
        if (campaign_template) {
            this.campaignTemplate = campaign_template.innerHTML;
        }

        this.userTimezone = utils.getCookie('browser_tz_guess') || dayjs.tz.guess();

        this.manageFilterMobile(utils);

        // Store cookies on first load
        this.store_fav_cookies();

        // Init the algolia client
        const searchClient = this.initSearchClient();
        if (!searchClient) return;

        // Configure instantsearch
        const search = this.configureInstantSearch(indexName, searchClient);

        // Custom global connectors.
        // Used to add them in multiple widget like the dynamic one.
        this.customRefinementList = this.createRefinementListRenderer();
        this.customHierarchicalMenu = this.createHierarchicalMenuRenderer();

        /**
         * Add the configure widget
         *
         * The configure widget lets you provide raw search parameters to the Algolia API without rendering anything.
         * Any props you add to this widget is forwarded to Algolia. For more information on the different parameters you can set, have a look at the search parameters API reference : https://www.algolia.com/doc/api-reference/search-api-parameters/.
         */
        this.setupGlobalSearchParameters(search);
        this.addConfigureWidget(search);

        // Add widgets for marketplace and products
        this.addMarketplaceWidget(search);

        // Add the menu select widget on top on the list of results
        this.addMenuSelectWidget(search);

        // Add dynamic widget : used for search page
        this.addDynamicWidgets(search);

        // Add classic facets on the left sidebar
        this.addFacetWidgets(search);

        // Add the main fonctional widgets
        this.addMainWidgets(search);

        // Start and init the search
        search.start();
    },

    initSearchClient: function () {
        const ALGOLIA_APP_ID = 'TQNO8H7E9L';
        const ALGOLIA_SEARCH_KEY = '2c549f4b4834223a81831950ebfe731f';

        if (!window['algoliasearch/lite']) {
            console.log("Client Algolia non disponible");
            return null;
        }

        const {liteClient: algoliasearch} = window['algoliasearch/lite'];
        return algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
    },

    setupGlobalSearchParameters: function () {
        const listType = this.algoliaConfig.list_type;

        this.searchParameters_filters = "";
        this.searchParameters_facetFilters = [];

        // We overwrite 'filters' in both cases
        // Todo : maybe think about mergeing previous 'filters' value. But useless for now, we don't need it.
        if (listType === 'favorite') {
            if (this.favorites_all_filter) {
                this.searchParameters_filters = this.favorites_all_filter;
            }
        } else {
            if (this.algoliaConfig['filters']) {
                // Already formated for Algolia in php
                this.searchParameters_filters = this.algoliaConfig['filters'];
            }
            if (this.algoliaConfig['facetFilters']) {
                this.algoliaConfig['facetFilters'].forEach(filter => {
                    // If the value is array it means we want OR operator so we follow algolia rules
                    if (filter.value instanceof Array) {
                        let or_filters = [];
                        this.searchParameters_facetFilters.push(or_filters);
                    } else {
                        this.searchParameters_facetFilters.push(`${filter.attribute}:${filter.value}`);
                    }
                });
            }
        }
    },

    configureInstantSearch: function (indexName, searchClient) {
        const {history} = instantsearch.routers;

        return instantsearch({
            indexName: indexName,
            searchClient,
            routing: {
                router: history(),
            },
            searchParams: {facets: ['*']},

            /**
             * Triggered when the state changes. This can be helpful for performing custom logic on a state change.
             *
             * When using onStateChange, the instance is under your control. You’re responsible for updating the UI state (with setUiState).
             *
             * This is triggered only when we make an action on anything : like clicking a facet.
             * This is NOT triggered when first loading.
             *
             * @param uiState
             * @param setUiState
             */
            onStateChange:({ uiState, setUiState }) => {
                // Store the fav cookies on all action
                this.store_fav_cookies();

                setUiState(uiState);
            }
        });
    },

    createConfigureRenderer: function (search) {
        const { connectConfigure } = instantsearch.connectors;

        const renderConfigure = (renderOptions, isFirstRender) => {
            const { refine, widgetParams } = renderOptions;
            const listType = this.algoliaConfig.list_type;

            // On the favorite page we only want to display the posts that the users has in his favorite cookies
            // So for Algolia we must add a filter on the posts ids to retrieve only those
            if (listType === 'favorite' && this.favorites_all_filter) {
                const searchFilters = widgetParams?.searchParameters?.filters;
                const hasSearchFilters = Boolean(searchFilters);
                const favoriteFilterHasChanged = this.favorites_all_filter !== searchFilters;
                if (hasSearchFilters && favoriteFilterHasChanged) {
                    // Must put all the default config
                    // If we ommit one it removes the value already setup
                    refine({
                        filters: this.favorites_all_filter ?? this.searchParameters_filters ?? "",
                        facetFilters: this.searchParameters_facetFilters ?? [],
                        hitsPerPage: this.algoliaConfig['hitsPerPage'] ?? 10,
                        ruleContexts: this.algoliaConfig['context'] ?? "default",
                        getRankingInfo: true,
                    });
                }
            }
        };

        return connectConfigure(renderConfigure);
    },

    createClearRefinementsRenderer: function (search) {
        const { connectClearRefinements } = instantsearch.connectors;

        const renderClearRefinements = (renderOptions, isFirstRender) => {
            const { refine, widgetParams } = renderOptions;

            if (isFirstRender) {
                widgetParams.container.innerHTML = `
                    <button class="button --full">
                        <span class="button__body">${ (window.weglot_lang== 'en') ? 'Clear' : 'Réinitialiser les filtres'}</span>
                    </button>
                `;

                widgetParams.container.querySelector('button').addEventListener('click', () => {
                    // Remove all facets
                    refine();

                    // Remove the searchBox query too
                    search.helper.setQuery('').search();

                    this.scroll_back_to_top();
                });
            }

            // Can't use that, because if we only type in searchBox the button will be disabled => the searchBox is not considered a refinement (facet)
            //widgetParams.container.querySelector('button').disabled = !canRefine;
        };

        return connectClearRefinements(renderClearRefinements);
    },

    createMenuSelectDatesRenderer: function (search) {
        const { connectMenu } = instantsearch.connectors;

        const renderMenuSelect = (renderOptions, isFirstRender) => {
            const { items, refine, widgetParams } = renderOptions;

            let is_refined = false;
            let date_list = [];

            widgetParams.container.innerHTML = `
                ${items.map((item) => {
                    const date = dayjs(item.value).startOf('day');
                    is_refined = is_refined || item.isRefined;
                    date_list.push(date);
                    return `
                      <button class="facetCard ${item.isRefined ? '--selected' : ''} js-algolia-menu-item" data-value="${item.value}">
                        <span class="facetCard__circle">
                          <span class="facetCard__circle__bigText">
                            ${date.format('D')}
                          </span>
                          <span class="facetCard__circle__smallText">
                            ${date.format('MMM')}
                          </span>
                        </span>
                        <span class="facetCard__text">
                          ${date.format('dddd')}
                        </span>
                      </button>
                    `;
                }).join('')}
            `;

            if (!is_refined) {
                const closestDateIndex = this.getClosestDateIndex(date_list);
                if (closestDateIndex !== false) {
                    refine(items[closestDateIndex].value);
                }
            }

            [...widgetParams.container.querySelectorAll('.js-algolia-menu-item')].forEach(element => {
                element.addEventListener('click', event => {
                    if (event.currentTarget.classList.contains('--selected')) {
                        event.preventDefault();
                        return;
                    }
                    refine(event.currentTarget.getAttribute('data-value'));
                });
            });
        };

        return connectMenu(renderMenuSelect);
    },

    createMenuSelectRenderer: function (search) {
        const { connectMenu } = instantsearch.connectors;

        const renderMenuSelect = (renderOptions, isFirstRender) => {
            const { items, refine, widgetParams } = renderOptions;

            let is_refined = false;

            let all_title = 'Tout';
            let all_picto = '';
            if (this.algoliaConfig.cpt_config['all']) {
                all_title = this.algoliaConfig.cpt_config['all']['title'];
                all_picto = this.algoliaConfig.cpt_config['all']['picto'];
            }

            widgetParams.container.innerHTML = `
                <button class="facetCard js-algolia-menu-item js-algolia-menu-item-all" data-value="">
                  ${all_picto ? `
                    <span class="facetCard__circle">
                      <img src="${all_picto}" alt="${all_title}">
                    </span>
                  ` : ''}
                  <span class="facetCard__text">
                    ${all_title}
                  </span>
                </button>
                ${items.map((item) => {
                    let title = item.value;
                    let picto = '';
                    if (this.algoliaConfig.cpt_config[item.value]) {
                        title = this.algoliaConfig.cpt_config[item.value]['title'];
                        picto = this.algoliaConfig.cpt_config[item.value]['picto'];
                    }
                    is_refined = is_refined || item.isRefined;
                    return `
                      <button class="facetCard ${item.isRefined ? '--selected' : ''} js-algolia-menu-item" data-value="${item.value}">
                        ${picto ? `
                          <span class="facetCard__circle">
                            <img src="${picto}" alt="${title}">
                          </span>
                        ` : ''}
                        <span class="facetCard__text">
                          ${title}
                        </span>
                      </button>
                    `;
                }).join('')}
            `;

            if (!is_refined) {
                widgetParams.container.querySelector('.js-algolia-menu-item-all').classList.add('--selected');
            }

            [...widgetParams.container.querySelectorAll('.js-algolia-menu-item')].forEach(element => {
                element.addEventListener('click', event => {
                    if (event.currentTarget.classList.contains('--selected')) {
                        event.preventDefault();
                        return;
                    }
                    refine(event.currentTarget.getAttribute('data-value'));
                });
            });
        };

        return connectMenu(renderMenuSelect);
    },

    createInfiniteHitsRenderer: function(search) {
        const { connectInfiniteHits } = instantsearch.connectors;

        const renderInfiniteHits = (renderOptions, isFirstRender) => {
            const { items, isFirstPage, isLastPage, showMore, showPrevious, results, widgetParams } = renderOptions;

            // Get the first element of the array (or empty string if array is empty or doesn't exist)
            const search_type_refinement = results?._state?.hierarchicalFacetsRefinements?.search_type?.[0] || 'all';
            this.setPostListCSSClass(search_type_refinement);

            if (isFirstRender) {
                document.querySelector('.js-algolia-hits-header').innerHTML = `
                    <button class="button --tertiary js-algolia-load-previous">
                        <span class="button__body">
                            ${this.algoliaConfig.load_prev_text}
                        </span>
                        <span class="button__loader uiLoader"></span>
                    </button>
                `;
                document.querySelector('.js-algolia-hits-footer').innerHTML = `
                    <button class="button js-algolia-load-more">
                        <span class="button__body">
                            ${this.algoliaConfig.load_more_text}
                        </span>
                        <span class="button__loader uiLoader"></span>
                    </button>
                `;

                document.querySelector('.js-algolia-load-more').addEventListener('click', () => {
                    showMore();
                });
                document.querySelector('.js-algolia-load-previous').addEventListener('click', () => {
                    showPrevious();
                });
            }

            const loadMoreButton = document.querySelector('.js-algolia-load-more');
            if (loadMoreButton) {
                if (isLastPage) {
                    loadMoreButton.classList.add('--hidden');
                } else {
                    loadMoreButton.classList.remove('--hidden');
                }
            }
            const loadPreviousButton = document.querySelector('.js-algolia-load-previous');
            const loadPreviousButtonContainer = document.querySelector('.js-algolia-hits-header');
            if (loadPreviousButton) {
                if (isFirstPage) {
                    loadPreviousButtonContainer.classList.add('--hidden');
                    loadPreviousButton.classList.add('--hidden');
                } else {
                    loadPreviousButtonContainer.classList.remove('--hidden');
                    loadPreviousButton.classList.remove('--hidden');
                }
            }

            // Reset currentSection when displaying sections titles
            this.currentSection = '';
            // Reset those variables to handles the campaign per lines count : only for exhibitors for now.
            this.currentLine = 0;
            this.currentLine_itemCount = 0;
            let exhibitor_campaign_displayed = false;

            // Update the list of results
            const items_length = items.length;
            widgetParams.container.innerHTML = `
                ${items.map((item, index) => {
                    let html = ``;
                
                    // The logic for exhibitor depends on stuff happening inside this.dispatchCardsTemplate(item);
                    // So the line counter is put inside this.setSectionLineVariables(item);
                    // And we put this before the function that inject the post html.
                    // This is because we must wait after we change the line, but before we display the new line.
                    this.setSectionLineVariables(item);
                    if (
                        this.campaignTemplate 
                        && item.post_type === 'exhibitor' 
                        && this.algoliaConfig.list_type === 'exhibitor_section_custom_field' 
                        && isFirstPage 
                        && !exhibitor_campaign_displayed
                    ) {
                        // For exhibitors : after 3rd line, but counting 4 because we depend on the previous process.
                        // or at the end if less => done at the end of this map function
                        if (this.currentLine === 4) {
                            exhibitor_campaign_displayed = true;
                            html += this.getCampaignHtml();
                        }
                    }

                    html += this.dispatchCardsTemplate(item, search_type_refinement);

                    // This logic applies after we add the card template
                    if (this.campaignTemplate && isFirstPage) {
                        if (item.post_type !== 'exhibitor') {
                            if ((items_length < 4 && index === items_length - 1) || index === 3) {
                                // Condition for article & programs
                                // After the 4th post or at the end if less than 4
                                html += this.getCampaignHtml();
                            }
                        } else if (
                            this.algoliaConfig.list_type === 'exhibitor_section_custom_field' 
                            && index === items_length - 1 
                            && !exhibitor_campaign_displayed
                        ) {
                            // For exhibitor if we're at the end and we haven't display the campaign already, we do it now.
                            html += this.getCampaignHtml();
                        } else if (this.algoliaConfig.list_type !== 'exhibitor_section_custom_field' ) {
                            if ((items_length < 9 && index === items_length - 1) || index === 8) {
                                // For exhibitor when we're on the classic alphabetical page display
                                // Simpler : display after 9 posts.
                                html += this.getCampaignHtml();
                            }
                        }
                    }
                    
                    return html;
                }).join('')}
            `;

            if (items_length > 0) {
                document.body.dispatchEvent(this.event_list_updated);
                if (isFirstPage) {
                    document.body.dispatchEvent(this.event_campaign_update);
                }
            }
        };

        return connectInfiniteHits(renderInfiniteHits);
    },


    createDynamicWidgetsRenderer: function () {
        const {connectDynamicWidgets} = instantsearch.connectors;

        const renderDynamicWidgets = (renderOptions) => {
            const {
                attributesToRender,
                widgetParams,
            } = renderOptions;

            // Clear the dynamic widget content
            widgetParams.container.innerHTML = '';

            attributesToRender.forEach(attribute => {
                if (!this.dynamicWidgets_containersList[attribute]) return;
                const container = this.dynamicWidgets_containersList[attribute];

                widgetParams.container.appendChild(container);

                // Add a separator between facets
                const separatorDiv = document.createElement('div');
                separatorDiv.className = 'listSide__separator';
                separatorDiv.innerHTML = '&nbsp;';

                widgetParams.container.appendChild(separatorDiv);
            });

            // Todo : Add gsap anim to facet from dynamic widget ?
            //self.bindDropdownFacetLevel1(widgetParams.container);
        };

        return connectDynamicWidgets(renderDynamicWidgets);
    },


    createRefinementListRenderer: function () {
        const {connectRefinementList} = instantsearch.connectors;
        const self = this;

        const renderRefinementList = (renderOptions, isFirstRender) => {
            const {
                items,
                refine,
                widgetParams,
            } = renderOptions;

            // Todo : Maybe we can keep the facet --opened status between renders ? Maybe on isFirstRender we render the facet container, and the rest of the time we only render facets. Must handle differently the hide/show when we have 0 available facet inside.

            if (isFirstRender) {
                widgetParams.container.innerHTML = `
                    <div class="facetHead js-algolia-facet-head ${widgetParams.unfold ? '--opened' : ''}">
                        <div class="facetHead__title">${widgetParams.title}</div>
                        <div class="facetHead__caret">
                            <svg class="icon icon-caret-bottom facetHead__caret__icon">
                                <use href="#icon-caret-bottom"></use>
                            </svg>
                        </div>
                    </div>
                    <div class="facetList --lvl1 js-algolia-facet-list ${widgetParams.unfold ? '--opened' : ''}"></div>
                `;

                self.bindDropdownFacetLevel1(widgetParams.container);
            }

            if (items.length <= 0) {
                widgetParams.container.classList.add('--empty');
            } else {
                widgetParams.container.classList.remove('--empty');
            }

            let containRefinedValue = false;
            widgetParams.container.querySelector('.js-algolia-facet-list').innerHTML = items.map((item, index) => {
                containRefinedValue = containRefinedValue || item.isRefined;
                return `
                    <div class="facetList__item js-algolia-facet-list-item">
                        <div class="inputGroup__checkbox__item facetList__item__input">
                            <input type="checkbox" class="inputGroup__checkbox__item__input" id="${widgetParams.attribute + '-' + index}" value="${self.escapeDoubleQuotes(item.value)}" ${item.isRefined ? 'checked' : ''}>
                            <label class="inputGroup__checkbox__item__label --flex" for="${widgetParams.attribute + '-' + index}">
                                ${item.label} <span class="inputGroup__checkbox__item__label__chip">${item.count}</span>
                            </label>
                        </div>
                    </div>
                `;
            }).join('');

            [...widgetParams.container.querySelectorAll('input')].forEach(element => {
                element.addEventListener('click', event => {
                    refine(event.currentTarget.value);

                    this.scroll_back_to_top();
                });
            });
        };

        return connectRefinementList(renderRefinementList);
    },

    createHierarchicalMenuRenderer: function () {
        const {connectHierarchicalMenu} = instantsearch.connectors;
        const self = this;

        const renderHierarchicalMenu = (renderOptions, isFirstRender) => {
            const {
                items,
                refine,
                widgetParams,
            } = renderOptions;

            // Todo : maybe we can always keep ALL facet values if we render on the isFirstRender. Maybe problems if we first render after reloading the page and we have already prefilter inside the url (history keeps the filters), meaning we will have a truncated facet list inside the widget and it wont ever be updated to full... Those are just some thoughts if we need this feature anyday...

            if (isFirstRender) {
                widgetParams.container.innerHTML = `
                    <div class="facetHead js-algolia-facet-head ${widgetParams.unfold ? '--opened' : ''}">
                        <div class="facetHead__title">${widgetParams.title}</div>
                        <div class="facetHead__caret">
                            <svg class="icon icon-caret-bottom facetHead__caret__icon">
                                <use href="#icon-caret-bottom"></use>
                            </svg>
                        </div>
                    </div>
                    <div class="facetList --lvl1 js-algolia-facet-list ${widgetParams.unfold ? '--opened' : ''}"></div>
                `;

                self.bindDropdownFacetLevel1(widgetParams.container);
            }

            if (items.length <= 0) {
                widgetParams.container.classList.add('--empty');
            } else {
                widgetParams.container.classList.remove('--empty');
            }

            widgetParams.container.querySelector('.js-algolia-facet-list').innerHTML = items.map((item, index) => `
                <div class="facetList__item js-algolia-facet-list-item">
                    <div class="inputGroup__checkbox__item facetList__item__input">
                        <input type="checkbox" class="inputGroup__checkbox__item__input" id="${widgetParams.attribute + '-' + index}" value="${self.escapeDoubleQuotes(item.value)}" ${item.isRefined ? 'checked' : ''}>
                        <label class="inputGroup__checkbox__item__label --flex" for="${widgetParams.attribute + '-' + index}">
                            <span>${item.label}</span> 
                            <span class="inputGroup__checkbox__item__label__chip">${item.count}</span>
                            <svg class="icon icon-caret-right facetList__item__caret ${item.isRefined ? '--opened' : ''}">
                                <use href="#icon-caret-right"></use>
                            </svg>
                        </label>
                    </div>
                    ${item.data ? `
                        <div class="facetList --lvl2">
                            ${item.data.map((item_lvl2, index_lvl2) => `
                                <div class="facetList__item">
                                    <div class="inputGroup__checkbox__item facetList__item__checkbox --lvl2">
                                        <input type="checkbox" class="inputGroup__checkbox__item__input" id="${widgetParams.attribute + '-' + index + '-' + index_lvl2}" value="${self.escapeDoubleQuotes(item_lvl2.value)}" ${item_lvl2.isRefined ? 'checked' : ''}>
                                        <label class="inputGroup__checkbox__item__label --flex" for="${widgetParams.attribute + '-' + index + '-' + index_lvl2}">
                                            ${item_lvl2.label} <span class="inputGroup__checkbox__item__label__chip">${item_lvl2.count}</span>
                                        </label>
                                    </div>
                                </div>
                            `).join('')}
                        </div>
                    ` : ''}
                </div>
            `).join('');

            [...widgetParams.container.querySelectorAll('input')].forEach(element => {
                element.addEventListener('click', event => {
                    refine(event.currentTarget.value);

                    this.scroll_back_to_top();
                });
            });
        };

        return connectHierarchicalMenu(renderHierarchicalMenu);
    },

	createHierarchicalProductCategoryRenderer: function (search) {
        const {connectHierarchicalMenu} = instantsearch.connectors;
        const self = this;

        const renderHierarchicalMenu = (renderOptions, isFirstRender) => {
            const {
                items,
                refine,
                widgetParams,
            } = renderOptions;

            if (items.length <= 0) {
                widgetParams.container.classList.add('--empty');
            } else {
                widgetParams.container.classList.remove('--empty');
            }

			// Cache initial page title/description on first render
			if (isFirstRender) {
				if (typeof self.pageBaseTitle === 'undefined') {
					const baseTitleEl = document.querySelector('.js-page-title');
					self.pageBaseTitle = baseTitleEl ? baseTitleEl.textContent : '';
				}
				if (typeof self.pageBaseDescription === 'undefined') {
					const baseDescEl = document.querySelector('.js-page-description');
					self.pageBaseDescription = baseDescEl ? baseDescEl.innerHTML : '';
				}
			}

            // Build breadcrumb from refined items
            const buildBreadcrumb = (items) => {
                const breadcrumb = [];
                
                const findRefinedPath = (items) => {
                    for (const item of items) {
                        if (item.isRefined) {
                            breadcrumb.push({ label: item.label, value: item.value });
                            if (item.data && item.data.length > 0) {
                                findRefinedPath(item.data);
                            }
                            break;
                        }
                    }
                };
                
                findRefinedPath(items);
                return breadcrumb;
            };

            // Find items to display (highest non-selected level)
            const getItemsToDisplay = (items) => {
                // If no items are refined, show all top-level items
                const hasRefinedItems = items.some(item => item.isRefined);
                if (!hasRefinedItems) {
                    return items;
                }

                // Find the deepest refined item with children
                const findDeepestRefinedWithChildren = (items) => {
                    for (const item of items) {
                        if (item.isRefined) {
                            if (item.data && item.data.length > 0) {
                                // Check if any child is also refined
                                const refinedChild = item.data.find(child => child.isRefined);
                                if (refinedChild && refinedChild.data && refinedChild.data.length > 0) {
                                    // Recursively check deeper levels
                                    const deeperResult = findDeepestRefinedWithChildren(item.data);
                                    return deeperResult || refinedChild.data;
                                } else if (refinedChild) {
                                    // If refined child has no children, show its siblings
                                    return [];
                                } else {
                                    // No child is refined, show children of current refined item
                                    return item.data;
                                }
                            }
                        }
                    }
                    return items;
                };

                return findDeepestRefinedWithChildren(items);
            };

			const breadcrumb = buildBreadcrumb(items);
			const itemsToDisplay = getItemsToDisplay(items);

			// Render breadcrumb from algoliaConfig.custom_breadcrumb
			const breadcrumbContainer = document.querySelector('.js-breadcrumb');
			if (breadcrumbContainer) {
				const baseCrumbs = Array.isArray(self.algoliaConfig?.custom_breadcrumb) ? self.algoliaConfig.custom_breadcrumb : [];
				const parts = [];
				const addPart = (html) => {
					if (parts.length > 0) parts.push(' &gt; ');
					parts.push(`<span>${html}</span>`);
				};

				// Render provided base breadcrumb items
				baseCrumbs.forEach((crumb, index) => {
					if (!crumb || !crumb.title) return;

					if (index === 0 && crumb.url) {
						addPart(`<a href="${self.escapeDoubleQuotes(crumb.url)}">${crumb.title}</a>`);
					} else if (Boolean(crumb.reset_filter)) {
						if (breadcrumb.length === 0) {
							addPart(`<span class="breadcrumb_last" aria-current="page">${crumb.title}</span>`);
						} else {
							addPart(`<button class="js-breadcrumb-reset">${crumb.title}</button>`);
						}
					}
				});

				// Append refined path
				if (breadcrumb.length > 0) {
					breadcrumb.forEach((crumb, index) => {
						const isLast = index === breadcrumb.length - 1;
						if (isLast) {
							addPart(`<span class="breadcrumb_last" aria-current="page">${crumb.label}</span>`);
						} else {
							addPart(`<button class="js-breadcrumb-item" data-value="${self.escapeDoubleQuotes(crumb.value)}">${crumb.label}</button>`);
						}
					});
				}

				breadcrumbContainer.innerHTML = parts.join('');

				// Click: reset to base (clear refinements)
				const resetEl = breadcrumbContainer.querySelector('.js-breadcrumb-reset');
				if (resetEl) {
					resetEl.addEventListener('click', (event) => {
						event.preventDefault();
                        refine('');
					});
				}

				// Click: go up one level (refine to selected breadcrumb value)
				[...breadcrumbContainer.querySelectorAll('.js-breadcrumb-item')].forEach(element => {
					element.addEventListener('click', event => {
						event.preventDefault();
                        // double refine() because doing the first one is like unchecking the item
                        // and doing the second one is like checking the item
						refine(event.currentTarget.getAttribute('data-value'));
						refine(event.currentTarget.getAttribute('data-value'));
					});
				});
			}

			// Update page title and description from config when refined
			const deepestCrumb = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1] : null;
			const pageTitleEl = document.querySelector('.js-page-title');
			const pageDescEl = document.querySelector('.js-page-description');
			if (deepestCrumb && self.algoliaConfig?.product_cat_infos) {
				const info = self.algoliaConfig.product_cat_infos[deepestCrumb.value];
				if (info) {
					if (pageTitleEl && typeof info.title === 'string') pageTitleEl.textContent = info.title;
					if (pageDescEl && typeof info.description === 'string') pageDescEl.innerHTML = info.description;
				}
			} else {
				// No refinement: restore base content
				if (pageTitleEl && typeof self.pageBaseTitle === 'string') pageTitleEl.textContent = self.pageBaseTitle;
				if (pageDescEl && typeof self.pageBaseDescription === 'string') pageDescEl.innerHTML = self.pageBaseDescription;
			}

            // Render the items to display
            widgetParams.container.innerHTML = itemsToDisplay.map((item) => `
                <button class="facetCategory ${item.isRefined ? '--selected' : ''} js-algolia-category-item" data-value="${self.escapeDoubleQuotes(item.value)}">
                    ${item.label}
                </button>
            `).join('');

            [...widgetParams.container.querySelectorAll('.js-algolia-category-item')].forEach(element => {
                element.addEventListener('click', event => {
                    if (event.currentTarget.classList.contains('--selected')) {
                        event.preventDefault();
                        return;
                    }
                    refine(event.currentTarget.getAttribute('data-value'));
                });
            });
        };

        return connectHierarchicalMenu(renderHierarchicalMenu);
    },

    addDynamicWidgets: function (search) {
        const dynamicWidgetsContainer = document.querySelector('.js-algolia-dynamic-facet');
        if (!dynamicWidgetsContainer) return;

        const customDynamicWidgets = this.createDynamicWidgetsRenderer();

        let isFirst = true;

        search.addWidgets([
            customDynamicWidgets({
                container: dynamicWidgetsContainer,
                widgets: [],
                fallbackWidget: ({ container, attribute }) => {
                    if (!this.algoliaConfig.facets_datas[attribute]) return;
                    const attr_datas = this.algoliaConfig.facets_datas[attribute];
                    const unfold = isFirst;
                    isFirst = false;

                    const facetDiv = document.createElement('div');
                    facetDiv.className = 'facet js-algolia-facet';
                    facetDiv.setAttribute('data-attribute', attribute);

                    this.dynamicWidgets_containersList[attribute] = facetDiv;

                    if (attr_datas['hierarchical']) {
                        return this.customHierarchicalMenu({
                            container: facetDiv,
                            attribute: attribute,
                            attributes: [
                                attribute + '.lvl0',
                                attribute + '.lvl1',
                            ],
                            title: attr_datas['label'] ?? attribute,
                            unfold: unfold,
                            limit: 1000,
                            operator: 'and'
                        });
                    } else {
                        return this.customRefinementList({
                            container: facetDiv,
                            attribute: attribute,
                            title: attr_datas['label'] ?? attribute,
                            unfold: unfold,
                            limit: 100,
                            operator: 'and'
                        });
                    }
                },
            })
        ]);
    },

    addFacetWidgets: function (search) {
        if (!this.algoliaConfig.facets) return;

        this.algoliaConfig.facets.forEach((facet, index) => {
            const container = document.querySelector('.js-algolia-facet-' + index);
            if (!container) return;

            if (facet.hierarchical) {
                search.addWidgets([
                    this.customHierarchicalMenu({
                        container,
                        attribute: facet.attribute,
                        attributes: [
                            facet.attribute + '.lvl0',
                            facet.attribute + '.lvl1',
                        ],
                        title: facet.label,
                        unfold: facet.unfold,
                        limit: 1000,
                        // TODO : test en cours pour forcer l'ordre des filtres remontés par algolia
                        //transformItems(items) {
                        //    console.log(items);
                        //    return items;
                        //},
                        operator: 'and'
                    })
                ]);
            } else {
                search.addWidgets([
                    this.customRefinementList({
                        container,
                        attribute: facet.attribute,
                        title: facet.label,
                        unfold: facet.unfold,
                        limit: 100,
                        sortBy: ['name:asc'],
                        operator: 'and'
                    })
                ]);
            }
        });
    },

    addMarketplaceWidget: function (search) {
        if (!this.algoliaConfig.product_category) return;

        const customProductCategoryHierarchicalMenu = this.createHierarchicalProductCategoryRenderer(search);
        const attribute = this.algoliaConfig.product_category.attribute;

        search.addWidgets([
            customProductCategoryHierarchicalMenu({
                container: document.querySelector('.js-algolia-product-category'),
                attribute: attribute,
                attributes: [
                    attribute + '.lvl0',
                    attribute + '.lvl1',
                    attribute + '.lvl2',
                    attribute + '.lvl3',
                    attribute + '.lvl4',
                    attribute + '.lvl5',
                    attribute + '.lvl6',
                    attribute + '.lvl7',
                    attribute + '.lvl8',
                    attribute + '.lvl9',
                ],
                title: this.algoliaConfig.product_category.label,
                limit: 1000,
                operator: 'and'
            })
        ]);
    },

    addMenuSelectWidget: function (search) {
        if (!this.algoliaConfig.menu_select) return;

        let customMenuSelect;
        if (this.algoliaConfig.menu_select.attribute === 'program_date') {
            customMenuSelect = this.createMenuSelectDatesRenderer(search);
        } else {
            customMenuSelect = this.createMenuSelectRenderer(search);
        }

        const filter_choices = this.algoliaConfig.menu_select.filter_choices ?? false;

        if (filter_choices) {
            search.addWidgets([
                customMenuSelect({
                    container: document.querySelector('.js-algolia-menu-select'),
                    attribute: this.algoliaConfig.menu_select.attribute,
                    limit: 100,
                    sortBy: ['name:asc'],
                    // Only display dates that are in the ACF field
                    transformItems(items) {
                        if (!filter_choices) {
                            return items;
                        }
                        return items.filter(item => filter_choices.includes(item.value));
                    },
                }),
            ]);
        }
    },

    addConfigureWidget: function (search) {
        const customConfigure = this.createConfigureRenderer(search);

        search.addWidgets([
            customConfigure({
                searchParameters: {
                    filters: this.searchParameters_filters ?? "",
                    facetFilters: this.searchParameters_facetFilters ?? [],
                    hitsPerPage: this.algoliaConfig['hitsPerPage'] ?? 10,
                    ruleContexts: this.algoliaConfig['context'] ?? "default",
                    getRankingInfo: true,
                },
            }),
        ]);
    },

    addMainWidgets: function (search) {
        const {searchBox} = instantsearch.widgets;
        const customClearAll = this.createClearRefinementsRenderer(search);
        const customInfiniteHits = this.createInfiniteHitsRenderer(search);

        search.addWidgets([
            searchBox({
                container: '.js-algolia-searchbox',
                placeholder: (window.weglot_lang== 'en') ? 'Search' : 'Recherche',
                showReset: false,
                cssClasses: {
                    root: 'inputGroup',
                    form: 'inputGroup__field --has-icon',
                    input: 'inputGroup__field__input inputNoZoom',
                    submit: 'inputGroup__field__icon',
                    reset: 'hidden'
                },
                templates: {
                    submit({cssClasses}, {html}) {
                        return html`
                            <svg class="icon icon-search">
                                <use href="#icon-search"></use>
                            </svg>`;
                    },
                },
                queryHook:(query, search) => {
                    search(query);
                    this.scroll_back_to_top();
                },
            }),
            customClearAll({
                container: document.querySelector('.js-algolia-clear-refinements')
            }),
            customInfiniteHits({
                container: document.querySelector('.js-algolia-hits'),
                showPrevious: true,
            }),
            this.createCounterWidget(),
        ]);
    },

    createCounterWidget: function () {
        return {
            init() {},
            render({ results }) {

				const button = document.querySelector('.js-list-side-open');

                if (results) {
                    if(button){
						const buttonAppend = button.querySelector('.js-button-append')

						buttonAppend.textContent = results.nbHits;
					}
                }
            }
        };
    },
  

    getCampaignHtml: function () {
        return `${this.campaignTemplate}`;
    },

    dispatchCardsTemplate: function (post, search_type_refinement = '') {
        if (!post || !post.post_type) {
            return '';
        }

        if (
            (this.algoliaConfig.list_type === 'search' || this.algoliaConfig.list_type === 'favorite')
            && (search_type_refinement === 'all' || search_type_refinement === 'page')
        ) {
            return this.renderSearch(post);
        } else if (post.post_type === 'program') {
            if (post.format === 'podcast') {
                return this.renderPodcast(post);
            } else {
                return this.renderProgram(post);
            }
        } else if (post.post_type === 'exhibitor') {
            if (this.algoliaConfig.list_type === 'exhibitor_section_custom_field') {
                return this.renderExhibitorWithSection(post);
            } else {
                return this.renderExhibitor(post);
            }
        } else if (post.post_type === 'article') {
            if (post?._rankingInfo?.promoted) {
                return this.renderArticlePromoted(post);
            } else {
                return this.renderArticle(post);
            }
        } else if (post.post_type === 'product') {
          return this.renderProduct(post);
        } else if (post.post_type === 'page') {
            return this.renderSearch(post);
        } else {
            return this.renderSearch(post);
        }

        return '';
    },

    renderArticlePromoted: function (post) {
        return `
            <article class="imageCard js-algolia-hitItem js-card-fav --verse" data-post-type="${post.post_type}" data-fav-id="${post.fav_id}">
              <div class="imageCard__favorites">
                <button class="button --circled --small --small-icon --white js-card-fav-button --icon-hover ${this.is_favorite(post.ID, post.post_type) ? '--active' : ''}" type="button">
                  <span class="button__prepend">
                    <svg class="icon main-icon icon-fav"><use href="#icon-fav"></use></svg>
                    <svg class="icon hover-icon icon-fav-filled"><use href="#icon-fav-filled"></use></svg>
                  </span>
                </button>
              </div>
              <a href="${post.link}" class="imageCard__link" title="${post.title}">
                <div class="imageCard__image">
                  <picture class="imageCardPicture">
                    <source srcset="${post.image.image_retina} 2x">
                    <img src="${post.image.image}" alt="${post.image.alt}" class="imageCardPicture__image imgAbsolute" loading="lazy">
                  </picture>
                  <div class="imageCard__image__overlay"></div>
                </div>
                <div class="imageCard__content">
                  <div class="imageCard__content__tags">
                    ${post['article-tag-1'] ? post['article-tag-1'].map(term => `
                      <div class="badge --small" ${term.badge_color ? 'style="--badge-bg: ' + term.badge_color + ';"' : ''}>${term.name}</div>
                    `).join('') : ''}
                  </div>
                  ${this.algoliaConfig['articleDisplayDate'] ? `
                    <div class="imageCard__content__date">${(window.weglot_lang== 'en') ? 'Published on :' : 'Publié le :'} ${post.date_display}</div>
                  ` : ''}
                  <h2 class="imageCard__content__title">${post.title}</h2>
                  <div class="imageCard__content__text">${post.chapo}</div>
                </div>
              </a>
            </article>
        `;
    },

    renderArticle: function (post) {
        return `
            <article class="articleCard js-algolia-hitItem js-card-fav --verse" data-post-type="${post.post_type}" data-fav-id="${post.fav_id}">
              <div class="articleCard__favorites">
                <button class="button --circled --small --small-icon --white js-card-fav-button --icon-hover ${this.is_favorite(post.ID, post.post_type) ? '--active' : ''}" type="button">
                  <span class="button__prepend">
                    <svg class="icon main-icon icon-fav"><use href="#icon-fav"></use></svg>
                    <svg class="icon hover-icon icon-fav-filled"><use href="#icon-fav-filled"></use></svg>
                  </span>
                </button>
              </div>
              <a href="${post.link}" class="articleCard__link" title="${post.title}">
                <div class="articleCard__image">
                  <picture class="articleCardPicture">
                    <source srcset="${post.image.image_retina} 2x">
                    <img src="${post.image.image}" alt="${post.image.alt}" class="articleCardPicture__image imgAbsolute" loading="lazy">
                  </picture>
                </div>
                <div class="articleCard__content">
                  <div class="articleCard__content__tags">
                    ${post['article-tag-1'] ? post['article-tag-1'].map(term => `
                      <div class="badge --small" ${term.badge_color ? 'style="--badge-bg: ' + term.badge_color + ';"' : ''}>${term.name}</div>
                    `).join('') : ''}
                  </div>
                  ${this.algoliaConfig['articleDisplayDate'] ? `
                    <div class="articleCard__content__date">${(window.weglot_lang== 'en') ? 'Published on :' : 'Publié le :'} ${post.date_display}</div>
                  ` : ''}
                  <h2 class="articleCard__content__title">${post.title}</h2>
                  <div class="articleCard__content__text">${post.chapo}</div>
                </div>
              </a>
            </article>
        `;
    },

    renderProduct: function (post) {
        return `
          <div class="productCard js-product-card js-algolia-hitItem  --${post.post_type} --link">
            <div class="productCard__fav js-fav"
                data-post-type="{{ product.post_type }}"
                data-fav-id="{{ product.id }}"
            >
              <button class="button --circled --small --small-icon --white js-card-fav-button --icon-hover ${this.is_favorite(post.ID, post.post_type) ? '--active' : ''}" type="button">
                <span class="button__prepend">
                  <svg class="icon main-icon icon-fav"><use href="#icon-fav"></use></svg>
                  <svg class="icon hover-icon icon-fav-filled"><use href="#icon-fav-filled"></use></svg>
                </span>
              </button>
            </div>
            <a href="${post.link}" class="productCard__link">
              <picture class="productCard__visual">
                <img src="${post.image.image}" alt="${post.image.alt}" data-image-id="6831" class="productCard__image" loading="lazy">
              </picture>
              <div class="productCard__content">
                  <div class="productCard__title">${post.title}</div>
                  ${post.exhibitor_title ? `<p class="productCard__exhibitor">${post.exhibitor_title}</p>` : ''}
              </div>
            </a>
          </div>
        `;
    },

    renderExhibitor: function (post) {
        return `
          <a href="${post.link}" class="logoCard js-algolia-hitItem js-card-fav --${post.post_type} --link" data-post-type="${post.post_type}" data-post-id="${post.ID}" data-fav-id="${post.fav_id}">
            <div class="logoCard__logo">
              <img src="${post.image.image}" alt="${post.image.alt}" class="logoCard__logo__image">
              <div class="logoCard__favorites">
                <button class="button --circled --small --small-icon --white js-card-fav-button --icon-hover ${this.is_favorite(post.ID, post.post_type) ? '--active' : ''}" type="button">
                  <span class="button__prepend">
                    <svg class="icon main-icon icon-fav"><use href="#icon-fav"></use></svg>
                    <svg class="icon hover-icon icon-fav-filled"><use href="#icon-fav-filled"></use></svg>
                  </span>
                </button>
              </div>
            </div>
            <div class="logoCard__content">
              <div class="logoCard__content__title">${post.title}</div>
              ${post.places_list ? `
                <div class="spot">
                  ${post.places_list.map((place, index) => 
                    `<div class="spotItem js-spot ${index < 2 ? '--active' : ''}">
                      <svg class="icon icon-pin-filled"><use href="#icon-pin-filled"></use></svg>
                      <span>${place.title}</span>
                    </div>`
                  ).join('')}
                </div>
                ${post.places_list.length > 2 ? `<button class="spotButton js-spot-button" data-post-id="${post.ID}" data-spot="${this.createSpotEncoder(post)}">${(window.weglot_lang== 'en') ? 'See all locations' : 'Voir tous les emplacements'}</button>` : ''}
              ` : ''}
            </div>
          </a>
        `;
    },

    renderSectionTitle: function (title) {
        return `
            <h2 class="listMain__sectionTitle">${title}</h2>
        `;
    },

    renderExhibitorWithSection: function (post) {
        let html = '';

        // Handle section title for promoted items
        if (post?._rankingInfo?.promoted) {
            if (!this.currentSection || this.currentSection !== 'promoted') {
                this.currentSection = 'promoted';
                html += this.renderSectionTitle(this.algoliaConfig.featured_exhibitors_title);
            }
        }
        // Handle section title for items with section attribute
        else if (post[this.algoliaConfig.section_attribute]) {
            if (!this.currentSection || this.currentSection !== post[this.algoliaConfig.section_attribute]) {
                this.currentSection = post[this.algoliaConfig.section_attribute];
                html += this.renderSectionTitle(post[this.algoliaConfig.section_attribute]);
            }
        }
        // Handle section title for items without section
        else {
            if (!this.currentSection || this.currentSection !== 'no_section') {
                this.currentSection = 'no_section';
                html += this.renderSectionTitle(this.algoliaConfig.exhibitors_without_section_title);
            }
        }

        html += this.renderExhibitor(post);

        return html;
    },

    renderProgram: function (post) {
        return `<a href="${post.link}" class="programCard js-algolia-hitItem js-card-fav" data-post-type="program" data-post-id="${post.ID}" data-fav-id="${post.fav_id}">
          <div class="programCard__media">
            ${post.image.image ? `
              <picture>
                <img src="${post.image.image}" alt="${post.image.alt}" class="programCard__media__image imgAbsolute" loading="lazy">
              </picture>
            ` : ''}
            <div class="programCard__favorites">
              <button class="button --circled --small --small-icon --white js-card-fav-button ${this.is_favorite(post.fav_id, post.post_type) ? '--active' : ''}" type="button">
                <span class="button__prepend">
                  <svg class="icon main-icon icon-fav"><use href="#icon-fav"></use></svg>
                  <svg class="icon hover-icon icon-fav-filled"><use href="#icon-fav-filled"></use></svg>
                </span>
              </button>
            </div>
            ${(post.format === 'vod' || post.format === 'live') ? `
              <div class="programCard__media__vod">
                <button class="buttonPlay">
                  <svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="buttonPlay__play"><path d="M14.7539 8.85268L0.396763 17.1418L0.396764 0.563578L14.7539 8.85268Z" fill="white"/></svg>
                </button>
              </div>
            ` : ''}
          </div>
          <div class="programCard__content">
            ${post.format !== 'vod' ? `
              <div class="programCardInfos">
                <div class="programCardInfos__item">
                  ${dayjs.unix(post.event_date.timestamp_start).tz(this.userTimezone).format('dddd D MMMM')}
                </div>
                ${post.format === 'live' ? `
                  <div class="programCardInfos__item --live">${(window.weglot_lang== 'en') ? 'Available live' : 'Disponible en live'}</div>
                ` : ''}
              </div>
            ` : ''}
            <h3 class="programCard__content__title">${post.title}</h3>
            ${post.description ? `
              <div class="programCard__content__description">
                <div class="readMoreText --html-structure">
                  ${post.description}
                </div>
              </div>
            ` : ''}
            ${post.linked_exhibitors ? `
              <div class="programCard__content__logos">
                ${post.linked_exhibitors.map(exhibitor => `
                  <div class="programCardLogo">
                    <img src="${exhibitor.logo.src}" alt="${exhibitor.logo.alt}" class="programCardLogo__image">
                  </div>
                `).join('')}
              </div>
            ` : ''}
            <div class="programCardDetails ${post.format === 'vod' ? '--full' : ''}">
              <div class="programCardDetailsContainer">
                ${post.format !== 'vod' ? `
                  <div class="programCardDetails__infos">
                    <div class="programCardDetails__infos__hours">
                      ${dayjs.unix(post.event_date.timestamp_start).tz(this.userTimezone).format('H:mm')} - ${dayjs.unix(post.event_date.timestamp_end).tz(this.userTimezone).format('H:mm')}
                    </div>
                    ${post.event_date.place_title ? `
                      <div class="programCardDetails__infos__localization">
                        <span><svg class="icon icon-pin-filled"><use href="#icon-pin-filled"></use></svg></span>
                        <span>${post.event_date.place_title}</span>
                      </div>
                    ` : ''}
                  </div>
                ` : ''}
                <div class="programCardDetails__cta">
                  <button class="button ${post.format === 'vod' ? '--full' : '--small'}" type="button">
                    <span class="button__body">
                      ${post.format === 'vod' ? 
                        ((window.weglot_lang== 'en') ? 'See'  : 'Voir la vidéo')
                        : 
                        ((window.weglot_lang== 'en') ? 'More' : 'Détail')
                      }
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </a>`;
    },

    renderPodcast: function (post) {
        return `<a href="${post.link}" class="podcastCard js-algolia-hitItem js-card-fav" data-post-type="program" data-post-id="${post.ID}" data-fav-id="${post.fav_id}">
          <div class="podcastCard__media">
            ${post.image.image ? `
              <picture>
                <img src="${post.image.image}" alt="${post.image.alt}" class="programCard__media__image" loading="lazy">
              </picture>
            ` : ''}
          </div>
          <div class="podcastCard__favorites">
            <button class="button --circled --small --small-icon --white js-card-fav-button ${this.is_favorite(post.fav_id, post.post_type) ? '--active' : ''}" type="button">
              <span class="button__prepend">
                <svg class="icon main-icon icon-fav"><use href="#icon-fav"></use></svg>
                <svg class="icon hover-icon icon-fav-filled"><use href="#icon-fav-filled"></use></svg>
              </span>
            </button>
          </div>
          <div class="podcastCard__content">
            <div class="podcastCard__infos">
              <span class="podcastCard__infos__date">
                ${dayjs.unix(post.event_date.timestamp_start).tz(this.userTimezone).format('dddd D MMMM')}
              </span>
              ${post.audio_duration ? `
                <div class="podcastCard__infos__duration">
                  <svg class="icon icon-chrono">
                    <use href="#icon-chrono"></use>
                  </svg>
                  ${post.audio_duration}
                </div>
              ` : ''}
            </div>
            <h3 class="podcastCard__content__title">${post.title}</h3>
            ${post.linked_exhibitors ? `
              <div class="podcastCard__content__logos">
                ${post.linked_exhibitors.map(exhibitor => `
                  <div class="podcastCardLogo">
                    <img src="${exhibitor.logo.src}" alt="${exhibitor.logo.alt}" class="podcastCardLogo__image">
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>
          ${post.audio_src ? `
            <div class="podcastCard__audio js-audio-widget">
              <div class="podcastCard__audio__button">
                <button class="buttonPlay js-audio-widget-button --relative --small --with-pause">
                  <svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="buttonPlay__play"><path d="M14.7539 8.85268L0.396763 17.1418L0.396764 0.563578L14.7539 8.85268Z" fill="white"/></svg>
                  <svg fill="none" viewBox="0 0 24 24" height="24" width="24" xmlns="http://www.w3.org/2000/svg" class="buttonPlay__pause"><path xmlns="http://www.w3.org/2000/svg" d="M9 6C9.55228 6 10 6.44772 10 7V17C10 17.5523 9.55228 18 9 18C8.44772 18 8 17.5523 8 17V7C8 6.44772 8.44772 6 9 6ZM15 6C15.5523 6 16 6.44772 16 7V17C16 17.5523 15.5523 18 15 18C14.4477 18 14 17.5523 14 17V7C14 6.44772 14.4477 6 15 6Z"></path></svg>
                </button>
              </div>
              <div class="podcastCard__audio__widget">
                <div class="podcastCardWaves js-audio-widget-waves" data-audio-url="${post.audio_src}" data-wave-color="#31B7BC" data-style="small"></div>
              </div>
            </div>
          ` : `
            <div class="programCardDetails">
              <div class="programCardDetailsContainer">
                <div class="programCardDetails__infos"></div>
                <div class="programCardDetails__cta">
                  <button class="button --small" type="button">
                    <span class="button__body">${(window.weglot_lang== 'en') ? 'More' : 'Détail'}</span>
                  </button>
                </div>
              </div>
            </div>
          `}
        </a>`;
    },

    renderSearch: function (post) {
        return `
          <article class="searchCard js-search-card --${post.post_type}" data-fav-id="${post.fav_id}">
            <a href="${post.link}" class="searchCard__inner" title="${post.title}">
              <div class="searchCardContent">
                <div class="searchCardContent__type badge --small">${post.badge_name}</div>
                <div class="searchCardContent__title">${post.title}</div>
                <div class="searchCardContent__infos">
                  ${post.post_type === 'program' ? `
                    <div class="searchCardContent__infos__date">
                      ${dayjs.unix(post.event_date.timestamp_start).tz(this.userTimezone).format('dddd D MMMM')}
                    </div>
                    ${post.format === 'podcast' && post.audio_duration ? `
                      <div class="searchCardContent__infos__time">
                        <span>
                          <svg class="icon icon-chrono"><use href="#icon-chrono"></use></svg>
                        </span>
                        <span>${post.audio_duration}</span>
                      </div>
                    ` : ''}
                    ${post.format === 'classic' ? `
                      <div class="searchCardContent__infos__hours">
                        ${dayjs.unix(post.event_date.timestamp_start).tz(this.userTimezone).format('H:mm')} - ${dayjs.unix(post.event_date.timestamp_end).tz(this.userTimezone).format('H:mm')}
                      </div>
                    ` : ''}
                  ` : ''}
                </div>
                ${post.event_date && post.event_date.place_title ? `
                  <div class="searchCardContent__localization">
                    <div class="spotItem --active">
                      <svg class="icon icon-pin-filled"><use href="#icon-pin-filled"></use></svg>
                      <span>${post.event_date.place_title}</span>
                    </div>
                  </div>
                ` : ''}
                ${post.extract ? `
                  <div class="searchCardContent__text wysiwyg">
                    ${post.extract}
                  </div>
                ` : ''}
                ${post.post_type === 'exhibitor' && post.places_list ? `
                  <div class="searchCardContent__spot spot">
                    ${post.places_list.map((place, index) => `
                      <div class="spotItem js-spot ${index < 2 ? '--active' : ''}">
                        <svg class="icon icon-pin-filled"><use href="#icon-pin-filled"></use></svg>
                        <span>${place.title}</span>
                      </div>
                    `).join('')}
                  </div>
                  ${post.places_list.length > 2 ? `<button class="spotButton js-spot-button" data-spot="${this.createSpotEncoder(post)}">${(window.weglot_lang== 'en') ? 'See all locations' : 'Voir tous les emplacements'}</button>` : ''}
                ` : ''}
              </div>
              ${post.format === 'podcast' ? `
                <div class="searchCardMedia --podcast">
                  <svg class="icon icon-onde"><use href="#icon-onde"></use></svg>
                </div>
              ` : post.post_type === 'page' || post.post_type === 'tab' ? `
                <div class="searchCardMedia --page">
                  <svg class="icon icon-page"><use href="#icon-page"></use></svg>
                </div>
              ` : `
                ${post.image.image ? `
                  <div class="searchCardMedia">
                    <picture>
                      <img src="${post.image.image}" alt="${post.image.alt}" class="programCard__media__image" loading="lazy">
                    </picture>
                    ${post.format === 'vod' ? `
                      <div class="searchCardMedia__vod">
                        <button class="buttonPlay">
                          <svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="buttonPlay__play"><path d="M14.7539 8.85268L0.396763 17.1418L0.396764 0.563578L14.7539 8.85268Z" fill="white"/></svg>
                        </button>
                      </div>
                    ` : ''}
                  </div>
                ` : ''}
              `}
            </a>
          </article>
        `;
    },

    /**
     * By default (with no class modifier) the css display 2 items per row
     * @param search_type_filter
     */
    setPostListCSSClass: function(search_type_filter) {
        const postList = document.querySelector('.js-algolia-hits');

        if (this.algoliaConfig.list_type === 'search' || this.algoliaConfig.list_type === 'favorite') {
            if (search_type_filter === 'exhibitor') {
                postList.classList.remove('--one');
                postList.classList.add('--three');
            } else if (search_type_filter === 'program' || search_type_filter === 'classic' || search_type_filter === 'live' || search_type_filter === 'podcast' || search_type_filter === 'vod' ) {
                postList.classList.remove('--one', '--three');
            } else {
                postList.classList.add('--one');
                postList.classList.remove('--three');
            }
        }
    },

    setSectionLineVariables: function(post) {
        // Handle section title for promoted items
        if (post?._rankingInfo?.promoted) {
            if (!this.currentSection || this.currentSection !== 'promoted') {
                this.currentLine_itemCount = 0;
                this.currentLine++;
            }
        }
        // Handle section title for items with section attribute
        else if (post[this.algoliaConfig.section_attribute]) {
            if (!this.currentSection || this.currentSection !== post[this.algoliaConfig.section_attribute]) {
                this.currentLine_itemCount = 0;
                this.currentLine++;
            }
        }
        // Handle section title for items without section
        else {
            if (!this.currentSection || this.currentSection !== 'no_section') {
                this.currentLine_itemCount = 0;
                this.currentLine++;
            }
        }

        this.currentLine_itemCount++;
        if ((this.currentLine_itemCount - 4) % 3 === 0 && this.currentLine_itemCount >= 4) {
            this.currentLine++;
        }
    },

    bindDropdownFacetLevel1: function (facet) {
        const facetHead = facet.querySelector('.js-algolia-facet-head');
        if (!facetHead) return;
        const facetList = facet.querySelector('.js-algolia-facet-list');
        if (!facetList) return;

        facetHead.addEventListener('click', function () {
            facetHead.classList.toggle('--opened');
            facetList.classList.toggle('--opened');
        });
    },

    escapeDoubleQuotes: function (str) {
        if (typeof str !== 'string') {
            return '';
        }

        // Convert double quotes html entity
        return str.replace(/"/g, '&quot;');
    },

    store_fav_cookies: function() {
        this.favorites = JSON.parse(this.utils.getCookie('favorites') || "[]");
        this.favorites_program = JSON.parse(this.utils.getCookie('favorites_program') || "[]");
        this.favorites_exhibitor = JSON.parse(this.utils.getCookie('favorites_exhibitor') || "[]");
        this.favorites_product = JSON.parse(this.utils.getCookie('favorites_product') || "[]");

        this.favorites_all = [...new Set([
            ...(this.favorites || []),
            ...(this.favorites_program || []),
            ...(this.favorites_exhibitor || []),
            ...(this.favorites_product || [])
        ])];

        // Do this once onStateChange
        // To avoid doing it twice on widget rendering
        this.favorites_all_filter = this.favorites_all.map(id => `fav_id:${id}`).join(' OR ');
    },

    createSpotEncoder: function (post) {
        return encodeURIComponent(JSON.stringify({
            image: post.image.image,
            alt: post.image.alt,
            title: post.title,
            description: post.description,
            places: post.places_list
        }));
    },

    is_favorite: function(post_id, post_type) {
        let favList;
        if (post_type === 'program') {
            favList = this.favorites_program;
        } else if (post_type === 'exhibitor') {
            favList = this.favorites_exhibitor;
        } else if (post_type === 'product') {
            favList = this.favorites_product;
        } else {
            favList = this.favorites;
        }

        if (!favList) return false;

        return favList.includes(String(post_id));
    },

    scroll_back_to_top: function() {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });

        // Pour l'instant on reste simple, à voir si on va pas tout en haut.
        /*const refTarget = document.querySelector('.js-algolia-scroll-back-to-top');
        if (refTarget) {
            const scrollTop = refTarget.getBoundingClientRect().top + window.scrollY;
            window.scrollTo(0, scrollTop);
        } else {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }*/
    },

    /**
     * Get the index of the closest date from an array of dayjs objects based on priority rules:
     * 1. Today's date if it exists in the array
     * 2. The closest future date if any exists
     * 3. The closest past date if no future dates exist
     * 4. We return false if nothing is found
     *
     * @param {Array<dayjs.Dayjs>} dates - Array of dayjs objects
     * @returns {number|boolean} The index of the closest date in the array or false if nothing found
     */
    getClosestDateIndex: function(dates) {
        if (!Array.isArray(dates) || dates.length === 0) return false;

        const today = dayjs().startOf('day');

        // Priority 1: Check if today's date exists in the array
        const todayIndex = dates.findIndex(date =>
            date && date.isValid() && date.isSame(today)
        );

        if (todayIndex !== -1) return todayIndex;

        // Create an array of valid dates with their original indices
        const validDatesWithIndices = dates
            .map((date, index) => ({ date, index }))
            .filter(item => item.date && item.date.isValid());

        if (validDatesWithIndices.length === 0) return false;

        // Sort by priority: future dates first, then by absolute difference
        validDatesWithIndices.sort((a, b) => {
            // First sort by future/past (future dates first)
            const aIsFuture = a.date.isAfter(today);
            const bIsFuture = b.date.isAfter(today);

            if (aIsFuture && !bIsFuture) return -1;
            if (!aIsFuture && bIsFuture) return 1;

            // Then sort by absolute difference
            return Math.abs(a.date.diff(today)) - Math.abs(b.date.diff(today));
        });

        // Return the index of the closest date according to our sorting
        return validDatesWithIndices[0].index;
    },

    manageFilterMobile: function(utils){

        const jsListSide = document.querySelector('.js-list-side-container');

        if (jsListSide) {
            const openListButton = jsListSide.querySelector('.js-list-side-open');
            const openListButtonBody = openListButton.querySelector('.js-button-body');
            const openListButtonText = openListButtonBody.textContent;
            const closeListButtonText = jsListSide.dataset.openText;

            const timeline = this.gsap.timeline({paused: true})
            const listSideItems = this.gsap.utils.toArray('.js-algolia-facet-item');

            timeline.set('.js-algolia-facets', {display: 'block'}, 'start')
                .from('.js-algolia-facets', {scaleY: 0, duration: 0.4, ease: 'power2.out'}, 'start')
                .from(listSideItems, {autoAlpha: 0, y: 40, duration: 0.3, ease: 'power2.out', stagger: 0.035}, '-=0.3')

            openListButton.addEventListener('click', () => {
                if (openListButton.classList.contains('--active')) {
                    timeline.timeScale(2).reverse()
                    openListButton.classList.remove('--active')
                    openListButtonBody.textContent = openListButtonText;
                    utils.enableScroll()
                } else {
                    timeline.timeScale(1).play()
                    openListButton.classList.add('--active')
                    openListButtonBody.textContent = closeListButtonText;
                    utils.disableScroll()
                }
            })

            // Only adjust on breakpoint crossing (>= 1021px vs < 1021px)
            const mq = window.matchMedia('(min-width: 1021px)');
            const syncTimelineToState = () => {
                const isActive = openListButton.classList.contains('--active');
                if (mq.matches) {
                    // Desktop: ensure the panel is visible and not animated (open state)
                    timeline.seek(1).pause();
                    utils.enableScroll();
                } else {
                    // Mobile: keep current state; do not force-close on keyboard resizes
                    if (isActive) {
                        timeline.seek(1).pause();
                    } else {
                        timeline.seek(0).pause();
                    }
                }
            };

            // Initial sync
            syncTimelineToState();

            // Listen only to breakpoint changes
            if (mq.addEventListener) {
                mq.addEventListener('change', syncTimelineToState);
            } else if (mq.addListener) {
                // Safari < 14
                mq.addListener(syncTimelineToState);
            }
        }

    },
}